import { VppaListingTemplate as template } from './vppa-listing.template';
import { VppaListingStyles as styles } from './vppa-listing.styles';
import { attr, customElement, FASTElement, observable } from '@microsoft/fast-element';
import { ZeroGridPro as zeroAgGrid } from '@genesislcap/foundation-zero-grid-pro';
import { INegotiationService, IVppaService } from '../../../../services';
import { IEnumService } from '../../../../services/enum.service';
import { INegotiation, IRfq, IVppa, Project } from '../../../../types';
import { Message, SocketSubscription } from '@genesislcap/foundation-comms';
import { updateArray } from '../../../../utils/dataserver';
import { CONTRACT_TYPE, PRODUCT_TYPE, VPPA_RFQ_STATUS } from '../../../../utils/enums/generic.enum';

const name = 'vppa-listing';

@customElement({
  name,
  template,
  styles,
})
export class VppaListing extends FASTElement {
  @IVppaService vppaService: IVppaService;
  @IEnumService enumService: IEnumService;
  @INegotiationService negotiationService: INegotiationService;

  @observable vppaGrid: zeroAgGrid;
  @observable vppaList: Array<IVppa> = [];

  @attr selectedRfq: IRfq;
  @attr projectSelected: Project;

  private vppaSubscription: SocketSubscription;
  private negotiationSubscription: SocketSubscription;
  private allNegotiations: INegotiation[] = [];
  @observable selectedVppa: string;

  connectedCallback(): void {
    super.connectedCallback();

    const gridOptions = {
      animateRows: true,
      suppressRowClickSelection: true,
      suppressBrowserResizeObserver: true,
      scrollbarWidth: 10,
    };

    this.vppaGrid.gridOptions = gridOptions;
  }

  disconnectedCallback(): void {
    this.vppaSubscription?.unsubscribe();
    this.negotiationSubscription?.unsubscribe();
    super.disconnectedCallback();
  }

  public selectVppa(vppa: IVppa) {
    if (vppa.VPPA_STATUS === VPPA_RFQ_STATUS.AWAITING_KEY_METRICS) {
      this.$emit('selectedVppa', vppa.VPPA_ID);
    } else {
      this.$emit('selectedVppa', vppa.VPPA_ID);
    }
    this.selectedVppa = vppa.VPPA_ID;
    this.vppaGrid.gridApi.redrawRows();
  }

  public selectedRfqChanged() {
    this.selectedVppa = null;
    if (!this.selectedRfq) {
      this.vppaGrid?.gridApi.setRowData(this.vppaList);
      return;
    } else {
      this.updateVppaStreaming()
      this.loadNegotiations(this.selectedRfq.RFQ_ID);
    }
  }

  public onGridReady() {
    this.vppaGrid.gridApi.setRowData(this.vppaList);
  }

  public projectSelectedChanged() {
    this.updateVppaStreaming();
  }

  private loadNegotiations(rfqId: string) {
    this.allNegotiations = [];
    const matchCriteria = `RFQ_ID == '${rfqId}' && PROJECT_ID == '${this.projectSelected.PROJECT_ID}'`;
    this.negotiationSubscription?.unsubscribe();
    this.negotiationSubscription = this.negotiationService
      .getAllNegotiations({
        CRITERIA_MATCH: matchCriteria,
      })
      .subscribe({
        next: (streamMessage: Message) => {
          const resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.allNegotiations = updateArray(resultList, this.allNegotiations);
            if (
              resultList.length === 1 &&
              resultList[0].DETAILS?.OPERATION === 'INSERT' &&
              resultList[0].RFQ_ID == this.selectedRfq.RFQ_ID &&
              resultList[0].VPPA_ID === this.selectedVppa
            ) {
              this.selectedVppa = null;
              this.$emit('proposalCreated');
            }
          }
          this.updateGrid();
        },
        error: (error: any) => {
          this.allNegotiations = [];
        },
      });
  }

  private updateGrid() {
    if (!this.selectedRfq) return;
    if (!this.allNegotiations || this.allNegotiations.length === 0) {
      this.vppaGrid?.gridApi.setRowData(this.vppaList);
      return;
    }
    const invalidVppas = this.allNegotiations.map((elm) => elm.VPPA_ID);

    const filteredVppas = this.vppaList.filter((vppa) => !invalidVppas.includes(vppa.VPPA_ID));

    this.vppaGrid?.gridApi.setRowData(filteredVppas);
  }

  private updateVppaStreaming(){
    this.selectedVppa = null;
    this.vppaList = [];
    if (this.selectedRfq) {
      const matchCriteria = `PROJECT_ID == '${this.projectSelected.PROJECT_ID}' 
      && (VPPA_STATUS == '${VPPA_RFQ_STATUS.ACTIVE}' || VPPA_STATUS == '${VPPA_RFQ_STATUS.EXISTING_INTEREST}') 
      && CONTRACT_TYPE == '${this.selectedRfq.CONTRACT_TYPE}'
      && ISO == '${this.selectedRfq.ISO}'
      && PRODUCT_TYPE == '${this.selectedRfq.PRODUCT_TYPE}'
      && (PRODUCT_TYPE == '${PRODUCT_TYPE.RECS}'|| SETTLEMENT_POINT == '${this.selectedRfq.SETTLEMENT_POINT}')`;
      this.vppaSubscription?.unsubscribe();
      this.vppaSubscription = this.vppaService.getAllVppas(matchCriteria).subscribe({
        next: (streamMessage: Message) => {
          const resultList = streamMessage.ROW ?? null;
          if (resultList) {
            this.vppaList = updateArray(resultList, this.vppaList);
          }
          this.updateGrid();
        },
        error: (error: any) => {
          this.vppaList = [];
        },
      });
    } else {
      this.vppaList = [];
    }
  }
}
